/**
 * The privacy notice — structure only. The words are the owner's to write.
 *
 * This portal processes UK personal data about identifiable people: names, home addresses, phone
 * numbers, email addresses, vet practices, insurers, emergency contacts. UK GDPR Article 13 says a
 * data subject must be told certain things at the point their data is collected, and "we will write
 * the policy after the first customer" is not one of the available options. So the container ships
 * with the portal and the copy drops into it.
 *
 * REACHABLE FROM BOTH SIDES OF THE DOOR. Signed out, it is linked from the sign-in screen and
 * renders standalone with its own back control — a person deciding whether to hand over their
 * details must be able to read this BEFORE signing in, which is the whole point of Article 13.
 * Signed in, it is a screen inside the shell, reached from the sidebar, and the navigation is the
 * way back — hence `onBack` being nullable rather than optional.
 */

import type { ReactElement } from 'react'

import { PRIVACY_SECTIONS } from './privacyCopy'

export interface PrivacyScreenProps {
  /**
   * Rendered as a back control when present. Null when the screen sits inside the app shell, where
   * the navigation already provides the way out.
   *
   * Nullable rather than optional: under `exactOptionalPropertyTypes` an optional prop cannot be
   * handed an explicit `undefined`, and both callers genuinely have a value to pass.
   */
  readonly onBack: (() => void) | null
}

export function PrivacyScreen({ onBack }: PrivacyScreenProps): ReactElement {
  const notice = <PrivacyNotice onBack={onBack} />

  // Signed in, `.ft-main` already provides the column this sits in. Signed out there is no shell at
  // all, so without a wrapper the card bled to the full width of the window while its text still
  // wrapped at 62ch — a strip of words down the left of an otherwise empty page.
  if (onBack === null) return notice

  return (
    <main
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'start center',
        padding: '26px 18px calc(26px + env(safe-area-inset-bottom))',
      }}
    >
      <div style={{ width: 'min(680px, 100%)' }}>{notice}</div>
    </main>
  )
}

function PrivacyNotice({ onBack }: PrivacyScreenProps): ReactElement {
  return (
    <section
      className="card elev-sm"
      style={{ gap: 0, padding: '30px 26px', animation: 'ft-rise .4s ease both' }}
    >
      {onBack === null ? null : (
        <button
          type="button"
          className="btn btn-ghost"
          onClick={onBack}
          style={{ minHeight: '44px', alignSelf: 'flex-start', marginBottom: '14px', fontSize: '13px' }}
        >
          {/* No icon: Organic's set has no left arrow, and T17 is not the task that adds one. */}
          Back to sign in
        </button>
      )}

      <div className="card-kicker">Privacy</div>
      <h1 style={{ margin: '0 0 8px', fontSize: 'clamp(24px, 4.5vw, 30px)' }}>
        How we look after your information
      </h1>
      <p className="text-muted" style={{ margin: '0 0 6px', fontSize: '14.5px', lineHeight: 1.6, maxWidth: '62ch' }}>
        This portal shows the record we hold about you and your dog. This page explains what is in
        that record, why we hold it, and what you can ask us to do about it.
      </p>

      <hr className="hr" style={{ margin: '22px 0' }} />

      <div style={{ display: 'grid', gap: '22px' }}>
        {PRIVACY_SECTIONS.map((section) => (
          <section key={section.id} aria-labelledby={`ft-privacy-${section.id}`}>
            <h2
              id={`ft-privacy-${section.id}`}
              style={{ margin: '0 0 6px', fontSize: '17px', lineHeight: 1.3 }}
            >
              {section.heading}
            </h2>
            <div style={{ display: 'grid', gap: '8px' }}>
              {section.paragraphs.map((paragraph) => (
                <p
                  key={paragraph}
                  className="text-muted"
                  style={{ margin: 0, fontSize: '13.5px', lineHeight: 1.6, maxWidth: '62ch' }}
                >
                  {paragraph}
                </p>
              ))}
            </div>
          </section>
        ))}
      </div>
    </section>
  )
}
